import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type UserlandWallet,
  type WireLimitOrder,
  fetchLimitOrders,
  patchLimitOrderRemote,
  putLimitOrder,
} from '../api/userland'
import {
  type LimitOrder,
  type PlaceLimitOrderInput,
  loadAllLimitOrders,
  mergeLimitOrders,
  newLimitOrder,
  saveLimitOrder,
} from '../lib/limitOrders'
import { type LimitSignerVault, enrollPrimaryLimitSigner } from '../lib/limitSigner'
import { LIMIT_EXECUTION_EVENT } from './useLimitExecutionNotifications'

function toWire(order: LimitOrder): WireLimitOrder {
  return {
    id: order.id,
    wallet: order.wallet,
    pair_addr: order.pairAddr,
    base_token: order.baseToken,
    ticker: order.ticker,
    dex: order.dex,
    side: order.side,
    amount_label: order.amountLabel,
    offer_nano: order.offerNano,
    limit_mcap_usd: order.limitMcapUsd,
    limit_price_ton: order.limitPriceTon,
    slippage_bps: order.slippageBps,
    retry_policy: order.retryPolicy?.enabled ? order.retryPolicy : undefined,
    preset_index: order.presetIndex,
    buy_slippage_bps: order.buySlippageBps,
    sell_slippage_bps: order.sellSlippageBps,
    status: order.status,
    fail_reason: order.failReason,
    tx_hash: order.txHash,
    triggered_mcap_usd: order.triggeredMcapUsd,
    created_at: order.createdAt,
    updated_at: order.updatedAt,
  }
}

function fromWire(row: WireLimitOrder): LimitOrder {
  return {
    id: row.id,
    wallet: row.wallet,
    pairAddr: row.pair_addr,
    baseToken: row.base_token,
    ticker: row.ticker,
    dex: row.dex,
    side: row.side,
    amountLabel: row.amount_label,
    offerNano: row.offer_nano,
    limitMcapUsd: row.limit_mcap_usd,
    limitPriceTon: row.limit_price_ton,
    slippageBps: row.slippage_bps,
    retryPolicy: row.retry_policy,
    retryState: row.retry_state,
    presetIndex: row.preset_index,
    buySlippageBps: row.buy_slippage_bps,
    sellSlippageBps: row.sell_slippage_bps,
    status: row.status,
    failReason: row.fail_reason,
    txHash: row.tx_hash,
    triggeredMcapUsd: row.triggered_mcap_usd,
    filledMcapUsd: row.filled_mcap_usd,
    filledAt: row.filled_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

async function persistRemote(order: LimitOrder): Promise<void> {
  if (order.status !== 'open' && order.status !== 'canceled') return
  try {
    await putLimitOrder(toWire(order))
  } catch {
    /* local book still holds the row */
  }
}

function notifyLimitBookChanged(): void {
  try {
    const ch = new BroadcastChannel('wils-limit-book')
    ch.postMessage('changed')
    ch.close()
  } catch {
    /* BroadcastChannel missing in some test/jsdom */
  }
}

const BOOK_POLL_MS = 4_000

export function useLimitOrders(
  wallet: UserlandWallet | null,
  vault: LimitSignerVault
): {
  orders: LimitOrder[]
  reload: () => Promise<void>
  place: (input: Omit<PlaceLimitOrderInput, 'wallet'>) => Promise<LimitOrder>
  cancel: (id: string) => Promise<void>
  openForPair: (pairAddr: string) => LimitOrder[]
} {
  const [orders, setOrders] = useState<LimitOrder[]>([])
  const walletAddress = wallet?.address ?? null

  const reload = useCallback(async () => {
    const local = await loadAllLimitOrders().catch(() => [] as LimitOrder[])
    let remote: LimitOrder[] = []
    try {
      remote = (await fetchLimitOrders()).map(fromWire)
    } catch {
      if (walletAddress) setOrders(local)
      return
    }
    const merged = mergeLimitOrders(local, remote)
    const remoteIds = new Set(remote.map((row) => row.id))
    for (const row of merged) {
      await saveLimitOrder(row).catch(() => {})
      const existing = remote.find((item) => item.id === row.id)
      if (!remoteIds.has(row.id) || (existing && row.updatedAt > existing.updatedAt)) {
        void persistRemote(row)
      }
    }
    setOrders(merged)
  }, [walletAddress])

  useEffect(() => {
    if (!walletAddress) {
      setOrders([])
      return
    }
    void reload()
    const onShow = () => {
      if (document.visibilityState === 'hidden') return
      void reload()
    }
    window.addEventListener('focus', onShow)
    window.addEventListener(LIMIT_EXECUTION_EVENT, onShow)
    document.addEventListener('visibilitychange', onShow)
    const poll = window.setInterval(onShow, BOOK_POLL_MS)
    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('wils-limit-book')
      ch.onmessage = () => {
        void reload()
      }
    } catch {
      ch = null
    }
    return () => {
      window.removeEventListener('focus', onShow)
      window.removeEventListener(LIMIT_EXECUTION_EVENT, onShow)
      document.removeEventListener('visibilitychange', onShow)
      window.clearInterval(poll)
      ch?.close()
    }
  }, [reload, walletAddress])

  const place = useCallback(
    async (input: Omit<PlaceLimitOrderInput, 'wallet'>) => {
      if (!wallet) throw new Error('Log in to place a limit order')
      await enrollPrimaryLimitSigner(wallet, vault)
      const draft = newLimitOrder({ ...input, wallet: wallet.address })
      // A local cache row cannot prove that the server accepted an executable order.
      const order = fromWire(await putLimitOrder(toWire(draft)))
      await saveLimitOrder(order).catch(() => {})
      setOrders((current) => [order, ...current.filter((row) => row.id !== order.id)])
      notifyLimitBookChanged()
      return order
    },
    [vault, wallet]
  )

  const cancel = useCallback(async (id: string) => {
    // A claimed order may already have been sent. The server must accept the
    // cancellation before its cache can say canceled; a 409 must never become PUT.
    const next = fromWire(await patchLimitOrderRemote(id, { status: 'canceled' }))
    await saveLimitOrder(next)
    setOrders((current) => current.map((row) => (row.id === id ? next : row)))
    notifyLimitBookChanged()
  }, [])

  const openForPair = useCallback(
    (pairAddr: string) =>
      orders.filter((row) => row.pairAddr === pairAddr && row.status === 'open'),
    [orders]
  )

  return useMemo(
    () => ({ orders, reload, place, cancel, openForPair }),
    [cancel, openForPair, orders, place, reload]
  )
}
